import API from '@dotcom/api-middleware';
import { QueryClient, queryOptions } from '@tanstack/react-query';
import { initializeResponse, digitalOrdersMock, successNoContentResponse } from '../data';
import {
  InitBody,
  InitializationData,
  DigitalOrderRequestBody,
  ConfirmProjectDetailsRequestBody,
  SuccessNoContentResponse,
} from '../global-types';
import { EnvelopeCode } from '../global-types/envelope';
import { DigitalOrderSubmission } from '../global-types/digital-orders';
import { ApiResponse } from '../global-types/services';
import { TransformedPersonalizationData } from '../utils/helper-settings';
import { getAccountIdFromCookie } from '../utils/utility/get-account-id-from-cookie';
import { processResponse } from './middleware';

const PODService = new API();
const mockResponse = process.env.REACT_APP_MOCK_INITIALIZATION_SERVICE === 'true';

export const initialize = (initBody: InitBody): Promise<ApiResponse<InitializationData>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<InitializationData> = initializeResponse;
      resolve(response);
    } else {
      const response = PODService.post('/customization', `/${accountId}/initialize`, initBody);
      processResponse(response, resolve, reject);
    }
  });
};

export const loadProject = (projectId: string): Promise<ApiResponse<InitializationData>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<InitializationData> = initializeResponse;
      resolve(response);
    } else {
      const response = PODService.get('/customization', `/${accountId}/${projectId}/load`);
      processResponse(response, resolve, reject);
    }
  });
};

export const updateProject = (
  data: { envelope_type?: EnvelopeCode; delivery_mode?: string; quantity?: number },
  projectId: string | undefined,
): Promise<SuccessNoContentResponse<null>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response = successNoContentResponse;
      resolve(response);
    } else {
      const response = PODService.patch('customization/', `${accountId}/${projectId}/update`, data);
      processResponse(response, resolve, reject);
    }
  });
};

export const updateProductQuantity = (productQuantity: string, projectId: string | undefined) => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response = successNoContentResponse;
      resolve(response);
    } else {
      const response = PODService.patch('customization/', `${accountId}/${projectId}/update`, {
        purchase_quantity: Number(productQuantity),
      });
      processResponse(response, resolve, reject);
    }
  });
};

export const migrateProjects = (guestAccountId: string, ...projectIds: string[]) => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const responseMock = {
        meta: initializeResponse.meta,
        data: projectIds.map((id) => ({ project_id: id, status: false })),
      };
      resolve(responseMock);
    } else {
      const requestBody = {
        guest_account_id: guestAccountId,
        project_ids: projectIds,
      };
      const response = PODService.patch('/customization', `/${accountId}/migrate`, requestBody);
      processResponse(response, resolve, reject);
    }
  });
};

export const addProjectToCart = (projectId: string): Promise<ApiResponse<Record<string, never>>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const responseMock = {
        meta: initializeResponse.meta,
        data: {},
      };
      resolve(responseMock);
    }
    const response = PODService.patch('/customization', `/${accountId}/${projectId}/add-to-cart`);
    processResponse(response, resolve, reject);
  });
};

export const savePersonalization = (
  formData: TransformedPersonalizationData[],
  projectId: string,
): Promise<ApiResponse<Record<string, never>>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const responseMock = {
        meta: initializeResponse.meta,
        data: {},
      };
      resolve(responseMock);
      return;
    }
    const response = PODService.patch('/customization', `/${accountId}/${projectId}/save-personalization`, formData);
    processResponse(response, resolve, reject);
  });
};

export const updateDraftName = (
  draftName: string,
  projectId: string,
  productQuantity: string,
  isDraft: boolean,
): Promise<ApiResponse<Record<string, never>>> => {
  const accountId = getAccountIdFromCookie();
  const formData = {
    name: draftName,
    is_draft: isDraft,
    purchase_quantity: Number(productQuantity),
  };

  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const responseMock = {
        meta: initializeResponse.meta,
        data: {},
      };
      resolve(responseMock);
      return;
    }
    const response = PODService.patch('/customization', `/${accountId}/${projectId}/save-draft`, formData);
    processResponse(response, resolve, reject);
  });
};

/**
 *
 * @param requestBody
 * @returns JSON containing 'order_reference_id' field
 * which is used as the value for 'order_number' field
 * in PATCH call to confirm endpoint.
 */
export const sendDigitalOrder = (
  requestBody: DigitalOrderRequestBody,
): Promise<ApiResponse<Record<string, string>>> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(digitalOrdersMock);
      return;
    }

    const response = PODService.post('/order-management', `/${accountId}/digital-orders`, requestBody);
    processResponse(response, resolve, reject);
  });
};

/** Profile service response shape (snake_case) */
type ProfileServiceResponse = {
  email_address: string;
  first_name: string;
  last_name: string;
  nick_name: string;
  uid: string;
  alternate_uid: string;
  cr_number: number;
  cid: number;
  date_of_birth: { month: number; day: number } | null;
  gender: string | null;
  address: Record<string, string> | null;
  opt_ins: {
    email_opt_in: boolean;
    postal_opt_in: boolean;
    sms_opt_in: boolean;
  };
  phone_number: string;
  hplus_membership_status: string | null;
};

/** Mapped profile with CamelCase fields for backward compatibility */
type MappedProfile = ProfileServiceResponse & {
  CRNumber: number;
  CID: number;
};

/**
 * Call to profile service to retrieve profile data
 * @returns JSON response object containing profile fields
 */
export const fetchAccountProfile = async (accountId = getAccountIdFromCookie()): Promise<MappedProfile | undefined> => {
  const res: ApiResponse<ProfileServiceResponse> = await PODService.get('/profile', `/${accountId}`);
  const profileData = res?.data;

  if (!profileData) {
    return undefined;
  }

  // Map snake_case fields to expected CamelCase format for backward compatibility
  const mappedProfile: MappedProfile = {
    ...profileData,
    CRNumber: profileData.cr_number,
    CID: profileData.cid,
  };

  return mappedProfile;
};

export const accountProfileQueryOptions = () => {
  const accountId = getAccountIdFromCookie();
  return queryOptions({
    queryKey: ['customization', accountId, 'profile'],
    queryFn: async () => (await fetchAccountProfile(accountId)) ?? null,
    staleTime: (query) =>
      query.state.data?.email_address &&
      Number.isFinite(query.state.data.CRNumber) &&
      Number.isFinite(query.state.data.CID)
        ? 60_000
        : 0,
    retry: false,
  });
};

/**
 * Patch call to confirm order has been sent
 * @param requestBody
 * @returns nothing
 * Request returns no response. Fire & forget.
 */
export const confirmProjectData = (requestBody: ConfirmProjectDetailsRequestBody): Promise<void> => {
  const accountId = getAccountIdFromCookie();
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve();
      return;
    }

    const response = PODService.patch('/customization', `/${accountId}/confirm`, requestBody);
    processResponse(response, resolve, reject);
  });
};

/**
 * Submits a digital order and confirms its project. When `submission` is
 * provided, the order reference is retained so a confirmation retry skips the
 * completed order POST.
 */
export const finalizeDigitalOrder = async (
  orderRequestBody: DigitalOrderRequestBody,
  confirmRequestBody: ConfirmProjectDetailsRequestBody,
  submission: DigitalOrderSubmission | undefined,
  queryClient: QueryClient,
): Promise<void> => {
  if (mockResponse) return;

  await PODService.validateTokens();

  if (submission?.orderReferenceId) {
    await confirmProjectData({ ...confirmRequestBody, order_number: submission.orderReferenceId });
    return;
  }

  const profileData = await queryClient.fetchQuery(accountProfileQueryOptions());

  if (!profileData) {
    if (submission) throw new Error('Account profile is required to send an eCard');
    return;
  }

  if (submission && !orderRequestBody.customer_no) {
    throw new Error('Customer number is required to send an eCard');
  }
  const finalOrderRequestBody = {
    ...orderRequestBody,
    customer_crown_card_number: profileData.CRNumber.toString(),
    enterprise_consumer_id: profileData?.CID?.toString() || '',
  };

  const orderData = await sendDigitalOrder(finalOrderRequestBody);

  if (!orderData?.data?.order_reference_id) {
    if (submission) throw new Error('Digital order response is missing its reference');
    return;
  }

  if (submission) submission.orderReferenceId = orderData.data.order_reference_id;

  const finalConfirmBody = {
    ...confirmRequestBody,
    order_number: orderData.data.order_reference_id,
  };

  await confirmProjectData(finalConfirmBody);
};

/**
 * Fetches the user profile data
 * @returns JSON response object containing profile fields
 */
export const getProfileData = async (): Promise<MappedProfile | undefined> => {
  const response = await fetchAccountProfile();
  return response;
};
